import { useState } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';

interface CreateBoardInlineProps {
  onCreate: (name: string, parentId?: string | null) => Promise<void>; // CHANGED signature
  parentId?: string | null;
  nestingLevel?: number;
}

export function CreateBoardInline({ onCreate, parentId = null, nestingLevel = 0 }: CreateBoardInlineProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

   const handleSubmit = async () => {
    if (!name.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onCreate(name.trim(), parentId);   // CHANGED: pass parentId
      setName('');
      setIsCreating(false);
    } catch (error) {
      logger.error('Failed to create board', error as Error, {
        name: name.trim(),
        parentId,
        nestingLevel,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setName('');
    setIsCreating(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!isCreating) {
    return (
      <button
        onClick={() => setIsCreating(true)}
        className={cn(
          "w-full flex items-center gap-2 px-3 py-2 text-sm",
          "text-white/50 hover:text-white",
          "hover:bg-white/10 rounded-md transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        )}
        style={{ paddingLeft: `${(nestingLevel * 20) + 12}px` }}
      >
        <Plus className="h-4 w-4" />
        <span>Create Board</span>
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-2 px-3 py-2"
      style={{ paddingLeft: `${(nestingLevel * 20) + 12}px` }}
    >
      <div className="h-4 w-4" />
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Board name"
        className="h-8 text-sm flex-1 bg-white/10 border-white/10 text-white placeholder:text-white/40"
        autoFocus
        disabled={isSubmitting}
      />
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
        onClick={handleSubmit}
        disabled={!name.trim() || isSubmitting}
      >
        <Check className="h-4 w-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
        onClick={handleCancel}
        disabled={isSubmitting}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
